import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Button, message, Spin, Alert, Select } from 'antd';
import { UserOutlined, DatabaseOutlined, FileTextOutlined, GlobalOutlined, ExclamationCircleOutlined, VideoCameraOutlined, ApiOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
// Removed unused imports: formatVietnamTime, formatVietnamDate, ensureArray
import axios from 'axios';

function Dashboard() {
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    tiktokAccounts: 0,
    tiktokVideos: 0,
    totalRequests: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');

  const fetchDashboardData = useCallback(async () => {
    try {
      console.log('📊 Fetching dashboard data for user:', user);
      setLoading(true);
      
      if (user.is_admin) {
        console.log('👑 Admin dashboard - fetching all data');
        // Admin dashboard - show system overview
        const [usersRes, tiktokAccountsRes, profileStatsRes] = await Promise.all([
          axios.get('/api/users/?skip=0&limit=1000'),
          axios.get('/api/tiktok-management/accounts'),
          axios.get('/api/profile/stats', { params: { time_filter: timeFilter } })
        ]);
        
        console.log('📊 Dashboard API responses:', {
          users: usersRes.data.data?.length || 0,
          tiktokAccounts: tiktokAccountsRes.data?.length || 0,
          profileStats: profileStatsRes.data
        });

        setStats({
          totalUsers: (usersRes.data.data || []).length,
          tiktokAccounts: (tiktokAccountsRes.data || []).length,
          tiktokVideos: profileStatsRes.data?.total_tiktok_videos || 0,
          totalRequests: 0
        });

        // Get recent TikTok accounts
        const recentTikTokAccounts = (tiktokAccountsRes.data || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        
        setRecentRequests(recentTikTokAccounts);
      } else {
        console.log('👤 User dashboard - fetching user data');
        // User dashboard - show only user's data
        const [tiktokAccountsRes, profileStatsRes] = await Promise.all([
          axios.get('/api/tiktok-management/accounts'),
          axios.get('/api/profile/stats', { params: { time_filter: timeFilter } })
        ]);
        
        console.log('📊 User TikTok accounts:', tiktokAccountsRes.data?.length || 0);
        
        setStats({
          totalUsers: 0,
          tiktokAccounts: (tiktokAccountsRes.data || []).length,
          tiktokVideos: profileStatsRes.data?.total_tiktok_videos || 0,
          totalRequests: 0
        });

        // Get user's recent TikTok accounts
        const recentTikTokAccounts = (tiktokAccountsRes.data || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        
        setRecentRequests(recentTikTokAccounts);
      }
      
    } catch (error) {
      console.error('❌ Dashboard error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      message.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user, timeFilter]);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted && user) {
        console.log('🔄 Dashboard useEffect triggered for user:', user);
        await fetchDashboardData();
      } else {
        console.log('⏭️ Skipping dashboard load:', { isMounted, user });
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [user, fetchDashboardData, timeFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#52c41a';
      case 'processing': return '#1890ff';
      case 'failed': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => <strong>@{text}</strong>,
    },
    {
      title: 'Nickname',
      dataIndex: 'nickname',
      key: 'nickname',
      ellipsis: true,
    },
    {
      title: 'Followers',
      dataIndex: 'follower_count',
      key: 'followers',
      render: (count) => count?.toLocaleString() || 0,
    },
    {
      title: 'Videos',
      dataIndex: 'video_count',
      key: 'videos',
      render: (count) => count?.toLocaleString() || 0,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive) => (
        <span style={{ color: isActive ? '#52c41a' : '#ff4d4f' }}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Dashboard</h1>
            <p>
              {user.is_admin 
                ? 'TikTok Management System Overview' 
                : 'Your TikTok Management Dashboard'
              }
            </p>
          </div>
          <Select
            value={timeFilter}
            onChange={setTimeFilter}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All' },
              { value: '1d', label: '1 Day' },
              { value: '7d', label: '7 Days' },
              { value: '1m', label: '1 Month' }
            ]}
          />
        </div>
      </div>
      

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {user.is_admin ? (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Users"
                  value={stats.totalUsers}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="TikTok Accounts"
                  value={stats.tiktokAccounts}
                  prefix={<VideoCameraOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title={`TikTok Videos ${timeFilter === 'all' ? '(All)' : `(${timeFilter === '1d' ? '1 Day' : timeFilter === '7d' ? '7 Days' : '1 Month'})`}`}
                  value={stats.tiktokVideos}
                  prefix={<VideoCameraOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="API Calls"
                  value={stats.totalRequests}
                  prefix={<ApiOutlined />}
                />
              </Card>
            </Col>
          </>
        ) : (
          <>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                <Statistic
                  title="My TikTok Accounts"
                  value={stats.tiktokAccounts}
                  prefix={<VideoCameraOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                <Statistic
                  title={`My Videos ${timeFilter === 'all' ? '(All)' : `(${timeFilter === '1d' ? '1 Day' : timeFilter === '7d' ? '7 Days' : '1 Month'})`}`}
                  value={stats.tiktokVideos}
                  prefix={<VideoCameraOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                <Statistic
                  title="API Calls"
                  value={stats.totalRequests}
                  prefix={<ApiOutlined />}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Card 
        title={user.is_admin ? "Recent TikTok Accounts" : "My Recent TikTok Accounts"} 
        extra={
          <Button onClick={fetchDashboardData} size="small">
            Refresh
          </Button>
        }
        className="recent-requests-card"
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={recentRequests || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 600 }}
          />
        </div>
      </Card>
    </div>
  );
}

export default Dashboard;